import type { RiskLevel } from "@/lib/api";

interface ScoreGaugeProps {
  score: number; // 0-10
  riskLevel: RiskLevel;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  animated?: boolean;
  className?: string;
}

// Color mapping by risk level - this is the source of truth
const RISK_COLORS: Record<RiskLevel, { main: string; glow: string }> = {
  critical: { main: "#ef4444", glow: "rgba(239, 68, 68, 0.4)" },      // red-500
  high: { main: "#f97316", glow: "rgba(249, 115, 22, 0.35)" },        // orange-500
  medium: { main: "#f59e0b", glow: "rgba(245, 158, 11, 0.3)" },       // amber-500
  low: { main: "#22d3ee", glow: "rgba(34, 211, 238, 0.3)" },          // cyan-400
  info: { main: "#10b981", glow: "rgba(16, 185, 129, 0.3)" },         // emerald-500
};

// Get color based on risk level (preferred)
function getRiskLevelColor(riskLevel: RiskLevel): string {
  return RISK_COLORS[riskLevel].main;
}

function getRiskLevelGlow(riskLevel: RiskLevel): string {
  return RISK_COLORS[riskLevel].glow;
}

const RISK_LABELS: Record<RiskLevel, string> = {
  critical: "CRITICAL",
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",
  info: "INFO",
};

export function ScoreGauge({
  score,
  riskLevel,
  size = "md",
  showLabel = true,
  animated = true,
  className = "",
}: ScoreGaugeProps) {
  const normalizedScore = Math.min(10, Math.max(0, score));
  const percentage = (normalizedScore / 10) * 100;

  // SVG dimensions based on size
  const dimensions = {
    sm: { size: 80, strokeWidth: 6, fontSize: 18, labelSize: 8 },
    md: { size: 120, strokeWidth: 8, fontSize: 28, labelSize: 10 },
    lg: { size: 160, strokeWidth: 10, fontSize: 36, labelSize: 12 },
  };

  const dim = dimensions[size];
  const radius = (dim.size - dim.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Use risk level for color (adjusted severity) rather than raw score
  const scoreColor = getRiskLevelColor(riskLevel);
  const glowColor = getRiskLevelGlow(riskLevel);

  return (
    <div
      className={`relative inline-flex flex-col items-center ${className}`}
      style={{ width: dim.size, height: dim.size + (showLabel ? 24 : 0) }}
    >
      {/* Glow effect behind the gauge */}
      <div
        className="absolute inset-0 rounded-full blur-xl opacity-50"
        style={{
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
        }}
      />

      <svg
        width={dim.size}
        height={dim.size}
        className="transform -rotate-90"
        style={{ filter: `drop-shadow(0 0 8px ${glowColor})` }}
      >
        {/* Background track */}
        <circle
          cx={dim.size / 2}
          cy={dim.size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={dim.strokeWidth}
          className="text-muted/30"
        />

        {/* Tick marks */}
        {[...Array(10)].map((_, i) => {
          const angle = (i / 10) * 360 - 90;
          const x1 =
            dim.size / 2 + (radius - dim.strokeWidth) * Math.cos((angle * Math.PI) / 180);
          const y1 =
            dim.size / 2 + (radius - dim.strokeWidth) * Math.sin((angle * Math.PI) / 180);
          const x2 =
            dim.size / 2 + (radius + 2) * Math.cos((angle * Math.PI) / 180);
          const y2 =
            dim.size / 2 + (radius + 2) * Math.sin((angle * Math.PI) / 180);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="currentColor"
              strokeWidth={1}
              className="text-muted/40"
            />
          );
        })}

        {/* Progress arc */}
        <circle
          cx={dim.size / 2}
          cy={dim.size / 2}
          r={radius}
          fill="none"
          stroke={scoreColor}
          strokeWidth={dim.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={animated ? "transition-all duration-1000 ease-out" : ""}
          style={{
            filter: `drop-shadow(0 0 4px ${scoreColor})`,
          }}
        />

        {/* Gradient overlay for depth */}
        <defs>
          <linearGradient id={`scoreGradient-${score}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.3" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle
          cx={dim.size / 2}
          cy={dim.size / 2}
          r={radius}
          fill="none"
          stroke={`url(#scoreGradient-${score})`}
          strokeWidth={dim.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={animated ? "transition-all duration-1000 ease-out" : ""}
        />
      </svg>

      {/* Center content */}
      <div
        className="absolute flex flex-col items-center justify-center"
        style={{
          top: dim.strokeWidth,
          left: dim.strokeWidth,
          right: dim.strokeWidth,
          bottom: showLabel ? dim.strokeWidth + 24 : dim.strokeWidth,
        }}
      >
        <span
          className="font-mono font-bold tracking-tight"
          style={{
            fontSize: dim.fontSize,
            color: scoreColor,
            textShadow: `0 0 20px ${glowColor}`,
          }}
        >
          {normalizedScore.toFixed(1)}
        </span>
        {showLabel && size !== "sm" && (
          <span
            className="font-mono font-semibold uppercase tracking-widest mt-0.5"
            style={{
              fontSize: dim.labelSize,
              color: scoreColor,
              opacity: 0.9,
            }}
          >
            {RISK_LABELS[riskLevel]}
          </span>
        )}
      </div>

      {/* Label below gauge for sm size */}
      {showLabel && size === "sm" && (
        <span
          className="font-mono font-semibold uppercase tracking-widest mt-1"
          style={{
            fontSize: dim.labelSize,
            color: scoreColor,
          }}
        >
          {RISK_LABELS[riskLevel]}
        </span>
      )}
    </div>
  );
}

// Mini score display for compact contexts
export function ScorePill({
  score,
  riskLevel,
}: {
  score: number;
  riskLevel: RiskLevel;
}) {
  // Use risk level for color consistency with ScoreGauge
  const color = getRiskLevelColor(riskLevel);

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-mono text-sm font-semibold"
      style={{
        backgroundColor: `${color}15`,
        color: color,
        border: `1px solid ${color}40`,
      }}
    >
      {score.toFixed(1)}
    </span>
  );
}

export default ScoreGauge;
